import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addModernization,
  deleteModernization,
  getModernizations,
  getUsers,
  updateModernization,
  type Modernization,
} from '../../api';
import type { User } from '../../types';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  SortConfig,
  SortValueKind,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import AddModernizationModal, {
  type AddModernizationPayload,
} from '../../components/AddModernizationModal/AddModernizationModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import RecordDetailsModal from '../../components/RecordDetailsModal/RecordDetailsModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import SortableTh from '../../components/SortableTh/SortableTh';
import { formatRub } from '../../utils/issueDisplay';
import '../IssuesPage/IssuesPage.css';
import '../FlightsHistoryPage/FlightsHistoryPage.css';

type ModernizationSortKey = keyof Modernization;

const sortKind = (key: ModernizationSortKey): SortValueKind => {
  switch (key) {
    case 'performed_at':
    case 'created_at':
      return 'date';
    case 'cost':
    case 'id':
    case 'created_by':
      return 'number';
    default:
      return 'string';
  }
};

const splitLabel = (item: Modernization, members: User[]) => {
  if (item.cost_split === 'personal') {
    const payer = members.find((user) => user.id === item.payer_user_id);
    return payer ? `Личный · ${payer.name}` : 'Личный';
  }
  if (item.cost_split === 'manual') return 'Вручную';
  return 'Согласно текущим долям';
};

const ModernizationPage = () => {
  const { canMaintain } = useAuth();
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [items, setItems] = useState<Modernization[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<Modernization | null>(null);
  const [editing, setEditing] = useState<Modernization | null>(null);
  const [deleting, setDeleting] = useState<Modernization | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig<ModernizationSortKey>>({
    key: 'performed_at',
    direction: 'desc',
  });

  const loadData = useCallback(async () => {
    try {
      const res = await getModernizations();
      setItems(res.data);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки модернизации'));
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, [aircraftId, dataRevision, loadData]);

  useEffect(() => {
    let cancelled = false;
    void getUsers()
      .then((res) => {
        if (!cancelled) setMembers(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [aircraftId]);

  const handleAdd = async (data: AddModernizationPayload) => {
    try {
      await addModernization(data);
      setAdding(false);
      await loadData();
      showSuccess('Запись добавлена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления модернизации'));
    }
  };

  const handleUpdate = async (data: AddModernizationPayload) => {
    if (!editing) return;
    try {
      await updateModernization(editing.id, data);
      setEditing(null);
      await loadData();
      showSuccess('Запись изменена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка изменения модернизации'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteModernization(deleting.id);
      setDeleting(null);
      await loadData();
      showSuccess('Запись удалена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления модернизации'));
    }
    setDeleteBusy(false);
  };

  const handleSort = (key: ModernizationSortKey) => {
    setSortConfig((prev) =>
      toggleSortConfig(prev, key, defaultDirectionForKind(sortKind(key)))
    );
  };

  const sortedItems = useMemo(
    () =>
      sortByConfig(items, sortConfig, (row, key) => row[key], sortKind, [
        { key: 'id', direction: 'desc' },
      ]),
    [items, sortConfig]
  );

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedItems.length,
    { resetKey: `${sortConfig.key}-${sortConfig.direction}` }
  );
  const visibleItems = sortedItems.slice(0, visibleCount);

  return (
    <div className="issues-page modernization-page">
      {feedbackNode}
      <div className="issues-header">
        <PageBackLink to="/condition">← Техническое состояние</PageBackLink>
        <h2>Модернизация</h2>
      </div>

      {canMaintain ? (
        <button
          type="button"
          className="page-cta-btn"
          onClick={() => setAdding(true)}
        >
          Добавить модернизацию
        </button>
      ) : null}

      <div className="flights-history-table-wrap">
        <table className="flights-history-table flights-history-table-compact">
          <thead>
            <tr>
              <SortableTh
                label="Когда"
                columnKey="performed_at"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Улучшение"
                columnKey="description"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Стоимость"
                columnKey="cost"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}
                >
                  Нет записей о модернизации
                </td>
              </tr>
            ) : (
              visibleItems.map((item) => (
                <tr
                  key={item.id}
                  className="flights-history-row"
                  onClick={() => setViewing(item)}
                >
                  <td>{DateFormat(item.performed_at)}</td>
                  <td className="record-row-main">{item.description}</td>
                  <td>{formatRub(item.cost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {hasMore ? (
        <LoadMoreButton
          remaining={remaining}
          nextBatch={nextBatch}
          onClick={loadMore}
          onShowAll={loadAll}
        />
      ) : null}

      {adding ? (
        <AddModernizationModal
          onClose={() => setAdding(false)}
          onConfirm={(data) => void handleAdd(data)}
        />
      ) : null}

      {editing ? (
        <AddModernizationModal
          title="Изменить модернизацию"
          confirmLabel="Сохранить"
          initial={{
            description: editing.description,
            performed_by: editing.performed_by,
            performed_at: editing.performed_at,
            cost: editing.cost,
            cost_split: editing.cost_split || 'shares',
            payer_ids: editing.payer_ids,
            payer_shares: editing.payer_shares,
            comment: editing.comment || '',
          }}
          onClose={() => setEditing(null)}
          onConfirm={(data) => void handleUpdate(data)}
        />
      ) : null}

      {viewing ? (
        <RecordDetailsModal
          title="Модернизация"
          summary={
            <>
              <span className="record-details-date">
                {DateFormat(viewing.performed_at)}
              </span>
              <span className="record-details-lead">{viewing.description}</span>
            </>
          }
          rows={[
            { label: 'Кем сделано', value: viewing.performed_by || '—' },
            { label: 'Стоимость', value: formatRub(viewing.cost) },
            { label: 'Тип расхода', value: splitLabel(viewing, members) },
          ]}
          note={viewing.comment}
          actions={
            canMaintain
              ? [
                  {
                    label: 'Изменить',
                    onClick: () => {
                      setEditing(viewing);
                      setViewing(null);
                    },
                  },
                  {
                    label: 'Удалить',
                    onClick: () => {
                      setDeleting(viewing);
                      setViewing(null);
                    },
                    danger: true,
                  },
                ]
              : []
          }
          onClose={() => setViewing(null)}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Удалить модернизацию?"
          message={
            deleting.cost != null && Number(deleting.cost) > 0
              ? `${deleting.description} от ${DateFormat(deleting.performed_at)}. Вместе с ней удалится связанный расход.`
              : `${deleting.description} от ${DateFormat(deleting.performed_at)}.`
          }
          confirmLabel="Удалить"
          danger
          submitting={deleteBusy}
          onCancel={() => {
            if (!deleteBusy) setDeleting(null);
          }}
          onConfirm={() => void handleDeleteConfirm()}
        />
      ) : null}
    </div>
  );
};

export default ModernizationPage;
